import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DataTable,
  FilterBar,
  PageHeader,
  SectionCard,
  StatusBadge,
  TabBar,
} from '@striker/ui-shell';
import { apiFetch } from '../auth/session';
import { StripeInvoicesTable, type InvoiceRow } from '../components/StripeInvoicesTable';

type TenantRow = {
  id: number;
  slug: string;
  business_name: string;
  plan_code?: string | null;
  account_status?: string | null;
  subscription_expires_at?: string | null;
  stripe_customer_id?: string | null;
};

type EntitlementDetail = {
  plan_code: string | null;
  modules: string[];
  catalog: Array<{ code: string; name: string; enabled: boolean }>;
  category?: {
    category_id: number;
    category_code: string;
    category_name: string;
    category_modules: string[];
  } | null;
};

type AuditRow = {
  id: number;
  action: string;
  actor_sub: string | null;
  created_at: string;
};

type OnboardingChecklistItem = {
  key: string;
  label: string;
  done: boolean;
  optional: boolean;
};

type OnboardingStatus = {
  tenant_id: number;
  business_name: string;
  category_code: string | null;
  category_name: string | null;
  current_step: number;
  max_step: number;
  completed_at: string | null;
  dismissed: boolean;
  setup_score: number;
  required_done: number;
  required_total: number;
  checklist: OnboardingChecklistItem[];
  updated_at: string;
};

function daysLeft(iso: string | null | undefined): string {
  if (!iso) return '—';
  const exp = new Date(iso).getTime();
  if (!Number.isFinite(exp)) return '—';
  const d = Math.ceil((exp - Date.now()) / (24 * 60 * 60 * 1000));
  if (d < 0) return `scaduto ${Math.abs(d)}g`;
  return `${d}g`;
}

type DetailTab = 'info' | 'modules' | 'billing' | 'audit' | 'onboarding';

export function MasterTenantsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [planDraft, setPlanDraft] = useState<Record<number, string>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('info');
  const [entitlements, setEntitlements] = useState<EntitlementDetail | null>(null);
  const [moduleDraft, setModuleDraft] = useState<Record<string, boolean>>({});
  const [feeCents, setFeeCents] = useState<number | ''>('');
  const [feeAvailable, setFeeAvailable] = useState(false);
  const [resvEnabled, setResvEnabled] = useState(false);
  const [resvCost, setResvCost] = useState<number | ''>('');
  const [resvUsage, setResvUsage] = useState<{
    total_count: number;
    period_count: number;
    period_amount_cents: number;
  } | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [nameDraft, setNameDraft] = useState('');
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null);

  async function loadTenants() {
    const res = await apiFetch('/api/v1/tenants');
    const body = await res.json().catch(() => []);
    if (!res.ok) {
      throw new Error(
        (body as { message?: string; error?: string }).message ||
          (body as { error?: string }).error ||
          `tenants ${res.status}`,
      );
    }
    const list = Array.isArray(body) ? (body as TenantRow[]) : [];
    setTenants(list);
    setPlanDraft((prev) => {
      const next = { ...prev };
      for (const t of list) {
        if (next[t.id] === undefined) next[t.id] = String(t.plan_code || 'free');
      }
      return next;
    });
  }

  useEffect(() => {
    void (async () => {
      try {
        await loadTenants();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  useEffect(() => {
    const raw = searchParams.get('storeId');
    if (!raw || tenants.length === 0) return;
    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0) return;
    if (selectedId === id) return;
    void loadDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tenants.length, selectedId]);

  async function loadDetail(storeId: number) {
    setSelectedId(storeId);
    setEntitlements(null);
    setInvoices([]);
    setAuditRows([]);
    setError(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('storeId', String(storeId));
        return next;
      },
      { replace: true },
    );
    try {
      const [entRes, feeRes, invRes, auditRes, onbRes, resvRes] = await Promise.all([
        apiFetch(`/api/v1/master/tenants/${storeId}/entitlements`),
        apiFetch(`/api/v1/master/tenants/${storeId}/billing-config`),
        apiFetch(`/api/v1/master/tenants/${storeId}/invoices`),
        apiFetch(`/api/v1/master/audit?storeId=${storeId}&limit=20`),
        apiFetch(`/api/v1/master/onboarding/status?storeId=${storeId}`),
        apiFetch(`/api/v1/master/tenants/${storeId}/reservations-config`),
      ]);
      const entBody = (await entRes.json().catch(() => ({}))) as EntitlementDetail & {
        message?: string;
        error?: string;
      };
      if (!entRes.ok) throw new Error(entBody.message || entBody.error || `entitlements ${entRes.status}`);
      setEntitlements(entBody);
      const draft: Record<string, boolean> = {};
      for (const m of entBody.catalog || []) draft[m.code] = Boolean(m.enabled);
      setModuleDraft(draft);

      const feeBody = (await feeRes.json().catch(() => ({}))) as {
        available?: boolean;
        reservation_fixed_fee_cents?: number | null;
      };
      setFeeAvailable(Boolean(feeBody.available));
      setFeeCents(
        feeBody.available && feeBody.reservation_fixed_fee_cents != null
          ? Number(feeBody.reservation_fixed_fee_cents)
          : '',
      );

      const invBody = (await invRes.json().catch(() => ({}))) as { invoices?: InvoiceRow[] };
      if (invRes.ok) setInvoices(Array.isArray(invBody.invoices) ? invBody.invoices : []);

      const resvBody = (await resvRes.json().catch(() => null)) as {
        enabled?: boolean;
        unit_cost_cents?: number;
        usage?: { total_count: number; period_count: number; period_amount_cents: number };
      } | null;
      if (resvBody) {
        setResvEnabled(Boolean(resvBody.enabled));
        setResvCost(resvBody.unit_cost_cents ?? 150);
        setResvUsage(resvBody.usage || null);
      }

      const auditBody = (await auditRes.json().catch(() => ({}))) as { actions?: AuditRow[] };
      if (auditRes.ok) setAuditRows(Array.isArray(auditBody.actions) ? auditBody.actions : []);

      const onbBody = (await onbRes.json().catch(() => ({}))) as { tenants?: OnboardingStatus[] };
      if (onbRes.ok && onbBody.tenants && onbBody.tenants.length > 0) {
        setOnboarding(onbBody.tenants[0]);
      } else {
        setOnboarding(null);
      }

      const tenant = tenants.find((t) => t.id === storeId);
      setNameDraft(tenant?.business_name || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function postAction(storeId: number, suffix: string, body?: unknown) {
    setBusyId(storeId);
    setMessage(null);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/master/tenants/${storeId}/${suffix}`, {
        method: 'POST',
        headers: body ? { 'content-type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const payload = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(payload.message || payload.error || `HTTP ${res.status}`);
      await loadTenants();
      if (selectedId === storeId) await loadDetail(storeId);
      setMessage(`Store ${storeId}: ${suffix} ok`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function saveModules(storeId: number) {
    setBusyId(storeId);
    setError(null);
    setMessage(null);
    try {
      const modules = Object.entries(moduleDraft).map(([code, enabled]) => ({
        module_code: code,
        enabled,
      }));
      const res = await apiFetch(`/api/v1/master/tenants/${storeId}/modules`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ modules }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      await loadDetail(storeId);
      setMessage(`Store ${storeId}: moduli aggiornati`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function saveFee(storeId: number) {
    setBusyId(storeId);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/v1/master/tenants/${storeId}/billing-config`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reservation_fixed_fee_cents: Number(feeCents) }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(`Store ${storeId}: fee aggiornata`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function saveReservationsConfig(storeId: number) {
    setBusyId(storeId);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/v1/master/tenants/${storeId}/reservations-config`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          reservation_unit_cost_cents: Number(resvCost),
          reservations_enabled: resvEnabled,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(`Store ${storeId}: config prenotazioni aggiornata`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function saveBusinessName(storeId: number) {
    setBusyId(storeId);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/v1/master/tenants/${storeId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ business_name: nameDraft.trim() }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      await loadTenants();
      setMessage(`Store ${storeId}: nome aggiornato`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function openCheckout(storeId: number) {
    setBusyId(storeId);
    setError(null);
    try {
      const planCode = planDraft[storeId] || 'free';
      const res = await apiFetch('/api/v1/master/billing/checkout-session', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ storeId, planCode }),
      });
      const body = (await res.json().catch(() => ({}))) as { url?: string; message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      if (!body.url) throw new Error('Checkout URL mancante');
      window.open(body.url, '_blank', 'noopener,noreferrer');
      setMessage(`Checkout aperto per store ${storeId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function openPortal(storeId: number) {
    setBusyId(storeId);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/tenant/billing/portal', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ storeId, returnUrl: `${window.location.origin}/master/tenants` }),
      });
      const body = (await res.json().catch(() => ({}))) as { url?: string; message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      if (!body.url) throw new Error('Portal URL mancante');
      window.open(body.url, '_blank', 'noopener,noreferrer');
      setMessage(`Portal aperto per store ${storeId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  const filtered = useMemo(() => {
    let result = tenants;
    if (statusFilter !== 'ALL') {
      if (statusFilter === 'NONE') {
        result = result.filter((t) => !String(t.account_status || '').trim());
      } else {
        result = result.filter((t) => String(t.account_status || '').toUpperCase() === statusFilter);
      }
    }
    // Category filter would need category data in TenantRow — skip if not available
    return result;
  }, [tenants, statusFilter]);

  const selected = tenants.find((t) => t.id === selectedId) || null;

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Aziende"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · controllo lifecycle ·{' '}
            <Link to="/master/billing">Billing registry</Link>
          </>
        }
        actions={
          <Link className="sk-btn" to="/master/tenants/new">
            Nuovo tenant
          </Link>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      {/* Filters + Table */}
      <SectionCard>
        <FilterBar>
          <label className="sk-field">
            <span className="sk-saas-hint">Stato billing</span>
            <select
              className="sk-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">Tutti</option>
              <option value="TRIAL">Trial</option>
              <option value="ACTIVE">Active</option>
              <option value="PAST_DUE">Past due</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="NONE">Senza account</option>
            </select>
          </label>
          <span className="sk-mc-hint">
            {filtered.length} / {tenants.length} tenant
          </span>
        </FilterBar>
        <DataTable
          emptyMessage="Nessun tenant"
          rows={filtered}
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'slug', label: 'Slug' },
            { key: 'business_name', label: 'Nome' },
            {
              key: 'account_status',
              label: 'Stato',
              render: (v, row) => (
                <span style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
                  <StatusBadge status={String(v || '')} />
                  {row.subscription_expires_at ? (
                    <span className="sk-mc-hint">{daysLeft(String(row.subscription_expires_at))}</span>
                  ) : null}
                </span>
              ),
            },
            {
              key: 'plan_code',
              label: 'Piano',
              render: (_v, row) => (
                <span className="sk-mc-hint">{String(row.plan_code || 'free')}</span>
              ),
            },
            {
              key: 'actions',
              label: 'Azioni',
              render: (_v, row) => {
                const status = String(row.account_status || '').toUpperCase();
                const busy = busyId === row.id;
                return (
                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {status === 'SUSPENDED' ? (
                      <button
                        type="button"
                        className="sk-btn"
                        disabled={busy}
                        onClick={() => void postAction(row.id, 'reactivate')}
                      >
                        Riattiva
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="sk-btn sk-btn--quiet"
                        disabled={busy || !status}
                        onClick={() => void postAction(row.id, 'suspend')}
                      >
                        Sospendi
                      </button>
                    )}
                    {status === 'TRIAL' || status === 'PAST_DUE' ? (
                      <button
                        type="button"
                        className="sk-btn sk-btn--quiet"
                        disabled={busy}
                        onClick={() => void postAction(row.id, 'extend-trial', { days: 7 })}
                      >
                        +7g trial
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="sk-btn sk-btn--quiet"
                      disabled={busy}
                      onClick={() => { void loadDetail(row.id); setActiveTab('info'); }}
                    >
                      Dettaglio
                    </button>
                    <button
                      type="button"
                      className="sk-btn sk-btn--quiet"
                      disabled={busy}
                      onClick={() => void openCheckout(row.id)}
                    >
                      Checkout
                    </button>
                    <button
                      type="button"
                      className="sk-btn sk-btn--quiet"
                      disabled={busy || !row.stripe_customer_id}
                      title={row.stripe_customer_id ? 'Apri Stripe Customer Portal' : 'Serve customer Stripe'}
                      onClick={() => void openPortal(row.id)}
                    >
                      Portal
                    </button>
                  </span>
                );
              },
            },
          ]}
        />
      </SectionCard>

      {/* Detail with tabs */}
      {selected ? (
        <SectionCard
          title={`Dettaglio · #${selected.id} ${selected.business_name}`}
          actions={<StatusBadge status={selected.account_status || ''} />}
        >
          <p className="sk-mc-hint" style={{ marginBottom: '0.5rem' }}>
            Piano effettivo: {entitlements?.plan_code || selected.plan_code || '—'} · slug{' '}
            <code>{selected.slug}</code>
            {entitlements?.category ? (
              <> · Categoria: <strong>{entitlements.category.category_name}</strong> ({entitlements.category.category_code})</>
            ) : (
              <> · Categoria: <em>non assegnata</em></>
            )}
          </p>

          <TabBar<DetailTab>
            tabs={[
              { key: 'info', label: 'Info' },
              { key: 'modules', label: 'Moduli', count: entitlements?.catalog?.length || 0 },
              { key: 'billing', label: 'Billing', count: invoices.length },
              { key: 'onboarding', label: 'Onboarding', count: onboarding ? onboarding.required_total - onboarding.required_done : 0 },
              { key: 'audit', label: 'Audit', count: auditRows.length },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />

          {/* Tab: Info */}
          {activeTab === 'info' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {entitlements?.category ? (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', maxWidth: '28rem' }}>
                  <label className="sk-field" style={{ flex: 1 }}>
                    <span>Cambia categoria</span>
                    <select
                      className="sk-input"
                      value={entitlements.category.category_code}
                      onChange={async (e) => {
                        const res = await apiFetch(`/api/v1/master/tenants/${selected.id}/category`, {
                          method: 'PUT',
                          headers: { 'content-type': 'application/json' },
                          body: JSON.stringify({ category_code: e.target.value }),
                        });
                        if (res.ok) void loadDetail(selected.id);
                      }}
                    >
                      <option value="restaurant">Ristorante</option>
                      <option value="beauty">Centro Estetico</option>
                    </select>
                  </label>
                  <span className="sk-mc-hint" style={{ paddingBottom: '0.5rem' }}>
                    ⚠ Cambia i moduli visibili
                  </span>
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: '0.5rem', maxWidth: '28rem' }}>
                <label className="sk-field" style={{ flex: 1 }}>
                  <span>Ragione sociale</span>
                  <input
                    className="sk-input"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="sk-btn"
                  style={{ alignSelf: 'end' }}
                  disabled={busyId === selected.id || !nameDraft.trim()}
                  onClick={() => void saveBusinessName(selected.id)}
                >
                  Salva nome
                </button>
              </div>

              {feeAvailable ? (
                <div style={{ display: 'flex', gap: '0.5rem', maxWidth: '20rem' }}>
                  <label className="sk-field" style={{ flex: 1 }}>
                    <span>Fee prenotazioni (centesimi)</span>
                    <input
                      className="sk-input"
                      type="number"
                      min={0}
                      max={100000}
                      value={feeCents}
                      onChange={(e) => setFeeCents(e.target.value === '' ? '' : Number(e.target.value))}
                    />
                  </label>
                  <button
                    type="button"
                    className="sk-btn"
                    style={{ alignSelf: 'end' }}
                    disabled={busyId === selected.id || feeCents === ''}
                    onClick={() => void saveFee(selected.id)}
                  >
                    Salva fee
                  </button>
                </div>
              ) : (
                <p className="sk-mc-hint">Fee prenotazioni non disponibile (schema restaurant assente).</p>
              )}
            </div>
          ) : null}

          {/* Tab: Modules */}
          {activeTab === 'modules' ? (
            <div>
              {entitlements ? (
                <>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))',
                      gap: '0.5rem',
                    }}
                  >
                    {(entitlements.catalog || []).map((m) => (
                      <label
                        key={m.code}
                        className="sk-field"
                        style={{ flexDirection: 'row', gap: '0.4rem', alignItems: 'center' }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(moduleDraft[m.code])}
                          onChange={(e) =>
                            setModuleDraft((prev) => ({ ...prev, [m.code]: e.target.checked }))
                          }
                        />
                        <span>
                          {m.name} <span className="sk-mc-hint">({m.code})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="sk-btn"
                    style={{ marginTop: '0.75rem' }}
                    disabled={busyId === selected.id}
                    onClick={() => void saveModules(selected.id)}
                  >
                    Salva moduli
                  </button>
                </>
              ) : (
                <p className="sk-mc-hint">Caricamento entitlements…</p>
              )}
            </div>
          ) : null}

          {/* Tab: Billing */}
          {activeTab === 'billing' ? (
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  className="sk-btn"
                  disabled={busyId === selected.id}
                  onClick={() => void openCheckout(selected.id)}
                >
                  Apri checkout
                </button>
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet"
                  disabled={busyId === selected.id || !selected.stripe_customer_id}
                  onClick={() => void openPortal(selected.id)}
                >
                  Stripe portal
                </button>
              </div>
              <h3 className="sk-mc-card__title" style={{ marginBottom: '0.5rem', marginTop: '1.5rem' }}>
                Prenotazioni (pay-per-reservation)
              </h3>
              <div className="sk-mc-card" style={{ padding: '0.75rem', marginBottom: '1rem' }}>
                <div className="sk-mc-kpi" style={{ marginBottom: '0.75rem' }}>
                  <div className="sk-mc-kpi__tile">
                    <p className="sk-mc-kpi__label">Stato</p>
                    <p className="sk-mc-kpi__value" style={{ fontSize: '0.95rem', color: resvEnabled ? '#16a34a' : '#dc2626' }}>
                      {resvEnabled ? 'Attivo' : 'Non attivo'}
                    </p>
                  </div>
                  <div className="sk-mc-kpi__tile">
                    <p className="sk-mc-kpi__label">Pren. totali</p>
                    <p className="sk-mc-kpi__value" style={{ fontSize: '0.95rem' }}>
                      {resvUsage?.total_count ?? '—'}
                    </p>
                  </div>
                  <div className="sk-mc-kpi__tile">
                    <p className="sk-mc-kpi__label">Pren. mese</p>
                    <p className="sk-mc-kpi__value" style={{ fontSize: '0.95rem' }}>
                      {resvUsage?.period_count ?? '—'}
                    </p>
                  </div>
                  <div className="sk-mc-kpi__tile">
                    <p className="sk-mc-kpi__label">Totale mese</p>
                    <p className="sk-mc-kpi__value" style={{ fontSize: '0.95rem' }}>
                      {resvUsage ? `€${(resvUsage.period_amount_cents / 100).toFixed(2)}` : '—'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <label className="sk-field" style={{ minWidth: '120px' }}>
                    <span>Costo/pren. (cents)</span>
                    <input
                      className="sk-input"
                      type="number"
                      min={0}
                      value={resvCost}
                      onChange={(e) => setResvCost(e.target.value === '' ? '' : Number(e.target.value))}
                    />
                  </label>
                  <label className="sk-field" style={{ minWidth: '100px' }}>
                    <span>Attivo</span>
                    <select
                      className="sk-input"
                      value={resvEnabled ? 'yes' : 'no'}
                      onChange={(e) => setResvEnabled(e.target.value === 'yes')}
                    >
                      <option value="no">No</option>
                      <option value="yes">Sì</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="sk-btn"
                    disabled={busyId === selected.id || resvCost === ''}
                    onClick={() => void saveReservationsConfig(selected.id)}
                  >
                    Salva config
                  </button>
                </div>
              </div>

              <h3 className="sk-mc-card__title" style={{ marginBottom: '0.5rem' }}>
                Fatture Stripe
              </h3>
              <StripeInvoicesTable
                invoices={invoices}
                refreshBasePath={`/api/v1/master/tenants/${selected.id}/invoices`}
                onRefreshed={(inv) =>
                  setInvoices((prev) =>
                    prev.map((p) => (p.stripe_invoice_id === inv.stripe_invoice_id ? inv : p)),
                  )
                }
              />
            </div>
          ) : null}

          {/* Tab: Audit */}
          {activeTab === 'audit' ? (
            <div>
              {auditRows.length ? (
                <ul className="sk-mc-feed">
                  {auditRows.map((row) => (
                    <li key={row.id} className="sk-mc-feed__item">
                      <span className="sk-mc-feed__dot" />
                      <div className="sk-mc-feed__body">
                        <p className="sk-mc-feed__action">{row.action}</p>
                        <p className="sk-mc-feed__meta">
                          {new Date(row.created_at).toLocaleString('it-IT')}
                          {row.actor_sub ? ` · ${row.actor_sub}` : ''}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="sk-mc-hint">Nessuna azione registrata.</p>
              )}
            </div>
          ) : null}

          {/* Tab: Onboarding */}
          {activeTab === 'onboarding' ? (
            <div>
              {onboarding ? (
                <>
                  <div className="sk-mc-onb-header">
                    <div className="sk-mc-onb-score">
                      <div className="sk-mc-onb-score__value">{onboarding.setup_score}%</div>
                      <div className="sk-mc-onb-score__label">Setup score</div>
                    </div>
                    <div className="sk-mc-onb-meta">
                      <p>
                        Step {onboarding.current_step}/{onboarding.max_step} ·{' '}
                        {onboarding.required_done}/{onboarding.required_total} step obbligatori
                      </p>
                      {onboarding.completed_at ? (
                        <p className="sk-mc-onb-done">Completato il {new Date(onboarding.completed_at).toLocaleDateString('it-IT')}</p>
                      ) : onboarding.dismissed ? (
                        <p className="sk-mc-hint">Banner dismiss dal tenant</p>
                      ) : (
                        <p className="sk-mc-hint">In corso</p>
                      )}
                    </div>
                    <div className="sk-mc-onb-actions">
                      <button
                        type="button"
                        className="sk-btn sk-btn--quiet"
                        onClick={async () => {
                          if (!selected) return;
                          try {
                            const res = await apiFetch(`/api/v1/master/onboarding/${selected.id}/auto-detect`, { method: 'POST' });
                            if (res.ok) {
                              const onbRes2 = await apiFetch(`/api/v1/master/onboarding/status?storeId=${selected.id}`);
                              const onbBody2 = (await onbRes2.json().catch(() => ({}))) as { tenants?: OnboardingStatus[] };
                              if (onbBody2.tenants && onbBody2.tenants[0]) setOnboarding(onbBody2.tenants[0]);
                              setMessage('Auto-detect eseguito');
                            }
                          } catch { /* ignore */ }
                        }}
                      >
                        Auto-detect
                      </button>
                      {!onboarding.completed_at ? (
                        <button
                          type="button"
                          className="sk-btn sk-btn--primary"
                          onClick={async () => {
                            if (!selected) return;
                            try {
                              const res = await apiFetch(`/api/v1/master/onboarding/${selected.id}/complete`, { method: 'POST' });
                              if (res.ok) {
                                const onbRes2 = await apiFetch(`/api/v1/master/onboarding/status?storeId=${selected.id}`);
                                const onbBody2 = (await onbRes2.json().catch(() => ({}))) as { tenants?: OnboardingStatus[] };
                                if (onbBody2.tenants && onbBody2.tenants[0]) setOnboarding(onbBody2.tenants[0]);
                                setMessage('Onboarding completato');
                              }
                            } catch { /* ignore */ }
                          }}
                        >
                          Completa
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="sk-mc-onb-progress">
                    <div className="sk-mc-onb-progress__bar" style={{ width: `${onboarding.setup_score}%` }} />
                  </div>

                  <ul className="sk-mc-onb-list">
                    {onboarding.checklist.map((item) => (
                      <li key={item.key} className={`sk-mc-onb-item${item.done ? ' sk-mc-onb-item--done' : ''}`}>
                        <span className="sk-mc-onb-item__check">{item.done ? '✓' : item.optional ? '○' : '○'}</span>
                        <span className="sk-mc-onb-item__label">{item.label}</span>
                        {item.optional ? <span className="sk-mc-onb-item__opt">opzionale</span> : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="sk-mc-hint">Nessuno stato onboarding per questo tenant.</p>
              )}
            </div>
          ) : null}
        </SectionCard>
      ) : null}
    </section>
  );
}
